/**
 * Get Started Card Component
 *
 * Card with a button to start the DA process by opening the DAISY Portal.
 */

import { LitElement, html, css } from 'lit';
import { baseStyles, cardStyles, buttonStyles } from '../styles/shared-styles.js';

/**
 * GetStartedCard - Start now button component
 * @element get-started-card
 * @fires get-started-click - When the Start now button is clicked
 */
export class GetStartedCard extends LitElement {
    static properties = {
        portalUrl: { type: String, attribute: 'portal-url' },
        buttonText: { type: String, attribute: 'button-text' },
        cardId: { type: String, state: true },
        isLoading: { type: Boolean, attribute: 'is-loading' },
        errorMessage: { type: String, attribute: 'error-message' }
    };

    static styles = [
        baseStyles,
        cardStyles,
        buttonStyles,
        css`
            :host {
                display: block;
                margin: var(--spacing-sm) 0;
                margin-left: 40px;
            }

            .get-started-card-container {
                display: flex;
            }

            .message-container {
                display: flex;
                width: 100%;
            }

            .get-started-btn {
                width: 100%;
                padding: 10px 24px;
                height: 40px;
                font-size: 14px;
                font-weight: 500;
                color: #ffffff;
                background: #111111;
                border: none;
                border-radius: 10px;
                cursor: pointer;
                transition: background-color 0.2s ease;
            }

            .get-started-btn:hover:not(:disabled) {
                background-color: #333333;
            }

            .get-started-btn:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }

            .get-started-btn--loading {
                position: relative;
                color: transparent;
            }

            .get-started-btn--loading::after {
                content: '';
                position: absolute;
                width: 16px;
                height: 16px;
                top: 50%;
                left: 50%;
                margin-left: -8px;
                margin-top: -8px;
                border: 2px solid #ffffff;
                border-radius: 50%;
                border-top-color: transparent;
                animation: spin 0.8s linear infinite;
            }

            @keyframes spin {
                to {
                    transform: rotate(360deg);
                }
            }

            .get-started-error {
                font-size: 13px;
                color: #dc3545;
                text-align: center;
                margin-top: 8px;
            }
        `
    ];

    constructor() {
        super();
        this.portalUrl = '';
        this.buttonText = 'Start now';
        this.cardId = `getStartedCard-${Date.now()}`;
        this.isLoading = false;
        this.errorMessage = '';
    }

    render() {
        const hasError = !!this.errorMessage;
        const buttonClasses = `get-started-btn ${this.isLoading ? 'get-started-btn--loading' : ''}`;

        return html`
            <div class="get-started-card-container" data-id=${this.cardId}>
                <div class="message-container">
                    <button class=${buttonClasses} @click=${this._handleClick} ?disabled=${this.isLoading}>
                        ${hasError ? 'Retry' : this.buttonText}
                    </button>
                </div>
            </div>
        `;
    }

    _handleClick() {
        if (this.isLoading) return;

        // Dispatch event for parent to handle BR creation and redirect
        // Parent will set isLoading=true, create BR, open URL, then remove card
        this.dispatchEvent(
            new CustomEvent('get-started-click', {
                detail: {
                    cardId: this.cardId,
                    portalUrl: this.portalUrl
                },
                bubbles: true,
                composed: true
            })
        );
    }
}

customElements.define('get-started-card', GetStartedCard);
